import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight,
  Pencil, Save, X, RotateCcw, Zap, History, Table2, ShieldCheck,
  ArrowLeft, RefreshCw, Info, Wand2, BookOpen, Scale, Upload, FileArchive, Download,
} from 'lucide-react';
import { reportingApi } from '../services/reportingApi';

// ── Utilitaires ───────────────────────────────────────────────────────────────

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const TOTAL_COLS = new Set([
  'Total', 'Total_Non_Res', 'Total_dev_etr', 'sous_total_res',
  'sous_total_nres_CEMAC', 'sous_total_nres_Autres', 'sous_total_nassfp', 'total',
]);

// ── Composants UI ─────────────────────────────────────────────────────────────

const Badge = ({ ok, count }) =>
  ok ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-700 rounded-full">
      <CheckCircle2 size={11} /> OK
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded-full">
      <XCircle size={11} /> {count} NOK
    </span>
  );

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <RefreshCw size={24} className="animate-spin text-[#004A99]" />
  </div>
);

const Alert = ({ type = 'info', children }) => {
  const styles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div className={`border px-4 py-3 text-sm rounded ${styles[type]}`}>{children}</div>
  );
};

// ── Modal de confirmation ─────────────────────────────────────────────────────

const ConfirmModal = ({ title, message, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600 mt-1">{message}</p>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} disabled={loading}
          className="px-4 py-2 text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 rounded disabled:opacity-50">
          Annuler
        </button>
        <button onClick={onConfirm} disabled={loading}
          className="px-4 py-2 text-sm bg-[#E20613] text-white hover:bg-[#c90511] rounded disabled:opacity-50 flex items-center gap-2">
          {loading && <RefreshCw size={14} className="animate-spin" />}
          Confirmer
        </button>
      </div>
    </div>
  </div>
);

// ── Onglet Données / Édition ──────────────────────────────────────────────────

const DataTab = ({ rows, columns, editMode, edits, onEditCell, nokPostes, cinterLoading }) => {
  if (!rows.length) return (
    <div className="px-6 py-12 text-center text-slate-400 text-sm">
      {cinterLoading
        ? <span className="flex items-center justify-center gap-2"><RefreshCw size={15} className="animate-spin text-blue-500" /> Génération des données en cours… cela peut prendre plusieurs minutes.</span>
        : 'Aucune donnée disponible. Assurez-vous que la production a été lancée pour cette date.'
      }
    </div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wide sticky left-0 bg-slate-50 z-10 min-w-[90px]">
              Poste
            </th>
            <th className="px-4 py-3 text-left font-semibold text-slate-600 text-xs uppercase tracking-wide min-w-[180px]">
              Libellé
            </th>
            {columns.map((col) => (
              <th
                key={col}
                className={`px-4 py-3 text-right font-semibold text-xs uppercase tracking-wide min-w-[120px] ${
                  TOTAL_COLS.has(col) ? 'text-blue-700 bg-blue-50' : 'text-slate-600'
                }`}
              >
                {col.replace(/_/g, ' ')}
              </th>
            ))}
            {editMode && <th className="px-3 py-3 w-6" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isNok = nokPostes.has(row.poste);
            const isModified = row.modified;
            return (
              <tr
                key={row.poste}
                className={`border-b border-slate-100 transition-colors ${
                  isNok ? 'bg-red-50/40' : isModified ? 'bg-amber-50/30' : 'hover:bg-slate-50'
                }`}
              >
                <td className="px-4 py-2.5 font-mono font-semibold text-slate-700 sticky left-0 bg-inherit z-10 flex items-center gap-1.5">
                  {isNok && <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />}
                  {row.poste}
                </td>
                <td className="px-4 py-2.5 text-slate-500 italic text-xs max-w-[200px] truncate" title={row.libelle}>
                  {row.libelle || <span className="text-slate-300">—</span>}
                </td>
                {columns.map((col) => {
                  const editKey = `${row.poste}__${col}`;
                  const isEdited = editKey in edits;
                  const isTotal = TOTAL_COLS.has(col);
                  const rawVal = isEdited ? edits[editKey] : (row.values[col] ?? '');

                  return (
                    <td
                      key={col}
                      className={`px-4 py-2 text-right font-mono ${
                        isTotal ? 'text-blue-800 font-semibold bg-blue-50/50' : 'text-slate-700'
                      } ${isEdited ? 'ring-1 ring-inset ring-amber-400 bg-amber-50' : ''}`}
                    >
                      {editMode && !isTotal ? (
                        <input
                          type="number"
                          step="any"
                          value={isEdited ? edits[editKey] : (row.values[col] ?? 0)}
                          onChange={(e) => onEditCell(row.poste, col, e.target.value)}
                          className="w-full text-right bg-transparent outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 min-w-[90px]"
                        />
                      ) : (
                        fmt(rawVal)
                      )}
                    </td>
                  );
                })}
                {editMode && (
                  <td className="px-2 py-2 text-slate-300 text-xs">
                    {isModified && <span title="Modifié">✎</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Onglet Contrôles CID ──────────────────────────────────────────────────────

const RuleSection = ({ rule, onAutoCorrect, autoLoading }) => {
  const [open, setOpen] = useState(!rule.all_ok);
  const nokRows = rule.postes.filter((p) => p.statut === 'NOK');

  return (
    <div className={`border rounded ${rule.all_ok ? 'border-slate-200' : 'border-red-200'}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          rule.all_ok ? 'hover:bg-slate-50' : 'bg-red-50 hover:bg-red-100'
        }`}
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
          <span className="text-xs font-mono text-slate-400 w-36 flex-shrink-0">{rule.rule_code}</span>
          <span className="text-sm text-slate-700">{rule.description}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {!rule.all_ok && rule.subtype !== 'global_sum' && rule.subtype !== 'conditional_min' && !rule.manual_only && (
            <button
              onClick={(e) => { e.stopPropagation(); onAutoCorrect(rule.rule_code); }}
              disabled={!!autoLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-[#E20613] text-white hover:bg-[#c90511] rounded disabled:opacity-50"
            >
              {autoLoading === rule.rule_code
                ? <RefreshCw size={12} className="animate-spin" />
                : <Zap size={12} />}
              Corriger auto
            </button>
          )}
          <Badge ok={rule.all_ok} count={rule.nok_count} />
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          {rule.all_ok ? (
            <p className="text-sm text-emerald-600 flex items-center gap-2">
              <CheckCircle2 size={14} /> Tous les postes respectent cette règle.
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded">
                <Info size={13} className="mt-0.5 flex-shrink-0" />
                <span>
                  {rule.type === 'CInter' ? (
                    <>
                      <strong>Règle :</strong> {rule.lhs_file}.{rule.lhs_col} = {rule.rhs_files?.map((f, i) => `${f}.${rule.rhs_cols?.[i] || ''}`).join(' + ')}.
                      La correction automatique ajuste la valeur RHS pour correspondre à la référence LHS.
                    </>
                  ) : (
                    <>
                      <strong>Règle :</strong> {rule.terms?.join(' + ')} = {rule.total_col}.
                      La correction automatique ajuste le terme de plus grande valeur absolue.
                    </>
                  )}
                </span>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="py-2 text-left font-medium">Poste</th>
                    <th className="py-2 text-right font-medium">Somme termes</th>
                    <th className="py-2 text-right font-medium">Total attendu</th>
                    <th className="py-2 text-right font-medium">Écart</th>
                  </tr>
                </thead>
                <tbody>
                  {nokRows.map((p) => (
                    <tr key={p.poste} className="border-b border-slate-100">
                      <td className="py-2 font-mono font-semibold text-red-700">{p.poste}</td>
                      <td className="py-2 text-right font-mono">{fmt(p.actual_sum)}</td>
                      <td className="py-2 text-right font-mono">{fmt(p.expected_total)}</td>
                      <td className="py-2 text-right font-mono font-semibold text-red-600">
                        {p.delta > 0 ? '+' : ''}{fmt(p.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CIDTab = ({ cidData, onAutoCorrect, autoLoading, cinterLoading }) => {
  if (!cidData) return (
    <div className="px-6 py-12 text-center text-slate-400 text-sm">
      Lancez les contrôles CID depuis le bouton ci-dessus.
    </div>
  );

  if (!cidData.has_rules) return (
    <Alert type="info">
      Aucune règle CID définie pour le fichier <strong>{cidData.fichier}</strong>. Les règles seront ajoutées progressivement.
    </Alert>
  );

  return (
    <div className="space-y-3">
      {cinterLoading && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          <RefreshCw size={12} className="animate-spin" />
          Chargement des règles CInter en cours…
        </div>
      )}
      {cidData.rules.map((rule) => (
        <RuleSection
          key={rule.rule_code}
          rule={rule}
          onAutoCorrect={onAutoCorrect}
          autoLoading={autoLoading}
        />
      ))}
    </div>
  );
};

// ── Onglet Historique ─────────────────────────────────────────────────────────

const ACTION_LABELS = {
  manual: { label: 'Manuel CID', color: 'text-blue-700 bg-blue-50' },
  auto: { label: 'Auto CID', color: 'text-purple-700 bg-purple-50' },
  direct_edit: { label: 'Édition directe', color: 'text-amber-700 bg-amber-50' },
};

const HISTORY_ACTION_FILTERS = [
  { key: '', label: 'Tous' },
  { key: 'manual', label: 'Manuel' },
  { key: 'auto', label: 'Auto' },
  { key: 'direct_edit', label: 'Édition directe' },
  { key: 'reimport', label: 'Réimport' },
];

const HistoryTab = ({ history, onRevert, onBatchRevert, revertLoading, batchRevertLoading }) => {
  const [actionFilter, setActionFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmBatch, setConfirmBatch] = useState(false);

  const filtered = useMemo(() => {
    if (!actionFilter) return history;
    return history.filter((e) => e.action === actionFilter);
  }, [history, actionFilter]);

  const activeCount = filtered.filter((e) => !e.reverted).length;
  const revertedCount = filtered.filter((e) => e.reverted).length;

  if (!history.length) return (
    <div className="px-6 py-12 text-center text-slate-400 text-sm">
      Aucune correction enregistrée pour ce fichier.
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {HISTORY_ACTION_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActionFilter(f.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                actionFilter === f.key
                  ? 'bg-[#004A99] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-2">
            {filtered.length} entrée(s) · {activeCount} active(s) · {revertedCount} annulée(s)
          </span>
        </div>
        {activeCount > 0 && (
          <button
            onClick={() => setConfirmBatch(true)}
            disabled={batchRevertLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded disabled:opacity-50"
          >
            {batchRevertLoading ? <RefreshCw size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Annuler tout ({activeCount})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Poste</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Colonne</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Ancienne val.</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Nouvelle val.</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Règle</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Par</th>
              <th className="px-4 py-3 w-24" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry) => {
              const act = ACTION_LABELS[entry.action] || { label: entry.action, color: 'text-slate-600 bg-slate-100' };
              const isReverted = entry.reverted;
              const isExpanded = expandedId === entry.id;
              return (
                <React.Fragment key={entry.id}>
                  <tr
                    className={`border-b border-slate-100 cursor-pointer ${isReverted ? 'opacity-50' : 'hover:bg-slate-50'} ${isExpanded ? 'bg-slate-50' : ''}`}
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  >
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {entry.done_at ? new Date(entry.done_at).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold text-slate-700">{entry.poste}</td>
                    <td className="px-4 py-3 font-mono text-slate-600 text-xs">{entry.col_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-500">{fmt(entry.old_value)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-800">{fmt(entry.new_value)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-400">{entry.rule_code}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${act.color}`}>
                        {act.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{entry.done_by || '—'}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {!isReverted ? (
                        <button
                          onClick={() => onRevert(entry.id)}
                          disabled={revertLoading === entry.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-slate-300 text-slate-600 hover:bg-slate-50 rounded disabled:opacity-50"
                          title="Annuler cette modification"
                        >
                          {revertLoading === entry.id
                            ? <RefreshCw size={11} className="animate-spin" />
                            : <RotateCcw size={11} />}
                          Annuler
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Annulé</span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={9} className="px-4 py-3 text-xs text-slate-500">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div><span className="text-slate-400">ID:</span> <span className="font-mono">{entry.id}</span></div>
                          <div><span className="text-slate-400">Date arrêté:</span> <span className="font-mono">{entry.closing_date}</span></div>
                          <div><span className="text-slate-400">Fichier:</span> <span className="font-mono">{entry.fichier}</span></div>
                          <div><span className="text-slate-400">Action:</span> <span className="font-mono">{entry.action}</span></div>
                          {entry.reverted && (
                            <>
                              <div><span className="text-slate-400">Annulé par:</span> {entry.reverted_by || '—'}</div>
                              <div><span className="text-slate-400">Annulé le:</span> {entry.reverted_at ? new Date(entry.reverted_at).toLocaleString('fr-FR') : '—'}</div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm batch revert modal */}
      {confirmBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmBatch(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Annuler toutes les corrections actives ?</h3>
            <p className="text-xs text-slate-500 mb-4">
              {activeCount} correction(s) seront annulées et les valeurs restaurées à leur état précédent.
              Cette action est traçable mais ne peut pas être elle-même annulée.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmBatch(false)}
                className="px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 rounded"
              >Annuler</button>
              <button
                onClick={() => { setConfirmBatch(false); onBatchRevert(actionFilter || null); }}
                disabled={batchRevertLoading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
              >
                {batchRevertLoading ? 'Annulation...' : `Confirmer (${activeCount})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page principale ───────────────────────────────────────────────────────────

const CIDPage = () => {
  const { closingDate, fichier } = useParams();
  const [searchParams] = useSearchParams();
  const decs = searchParams.get('decs') || '';
  const navigate = useNavigate();

  const [tab, setTab] = useState('data');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [rows, setRows] = useState([]);
  const [cidData, setCidData] = useState(null);
  const [history, setHistory] = useState([]);

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // CID state
  const [cidLoading, setCidLoading] = useState(false);
  const [cinterLoading, setCinterLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(null);
  const [confirmAuto, setConfirmAuto] = useState(null);

  // Corrections automatiques state
  const [cerberLoading, setCerberLoading] = useState(false);
  const [cerberNokDetails, setCerberNokDetails] = useState([]);  // détails fichiers NOK

  // Import ZIP / CERBER state
  const [zipUploading, setZipUploading] = useState(false);
  const [zipResult, setZipResult] = useState(null);
  const [cerberSaveToDb, setCerberSaveToDb] = useState(false);

  // History state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [revertLoading, setRevertLoading] = useState(null);
  const [batchRevertLoading, setBatchRevertLoading] = useState(false);

  // Columns derived from data
  const columns = useMemo(() => {
    if (!rows.length) return [];
    return Object.keys(rows[0]?.values || {});
  }, [rows]);

  // Which postes have NOK CID results
  const nokPostes = useMemo(() => {
    if (!cidData?.rules) return new Set();
    const set = new Set();
    for (const rule of cidData.rules) {
      for (const p of rule.postes || []) {
        if (p.statut === 'NOK') set.add(p.poste);
      }
    }
    return set;
  }, [cidData]);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCinterLoading(false);
    try {
      // Phase 1 : CIntra seulement — rapide (ne charge que le fichier principal)
      const r1 = await reportingApi.cidRun(closingDate, fichier, decs, false);
      const d1 = r1.data;
      setRows(d1.rows || []);
      setCidData(d1);
      setLoading(false);

      // Phase 2 : CInter complet — lent, en arrière-plan
      setCinterLoading(true);
      try {
        const r2 = await reportingApi.cidRun(closingDate, fichier, decs, true);
        setCidData(r2.data);
        setRows(r2.data.rows || []);
      } catch {
        // Phase 2 silencieuse : garder les résultats Phase 1
      } finally {
        setCinterLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des données CID.');
      setLoading(false);
    }
  }, [closingDate, fichier, decs]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await reportingApi.cidGetLog(closingDate, fichier);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [closingDate, fichier]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // ── Edit ────────────────────────────────────────────────────────────────────

  const handleEditCell = (poste, col, value) => {
    setEdits((prev) => ({ ...prev, [`${poste}__${col}`]: value }));
  };

  const handleCancelEdit = () => {
    setEdits({});
    setEditMode(false);
  };

  const handleSaveEdits = async () => {
    const editList = Object.entries(edits).map(([key, value]) => {
      const [poste, col_name] = key.split('__');
      return { poste, col_name, new_value: parseFloat(value) };
    }).filter((e) => !isNaN(e.new_value));

    if (!editList.length) { setEditMode(false); return; }

    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await reportingApi.cidSaveEdits(closingDate, fichier, editList);
      const d = res.data;
      setSaveMsg({ type: 'success', text: `${d.saved} valeur(s) enregistrée(s).${d.errors?.length ? ` ${d.errors.length} erreur(s).` : ''}` });
      setEdits({});
      setEditMode(false);
      await loadAll();
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  // ── CID ─────────────────────────────────────────────────────────────────────

  const handleRunCid = async () => {
    setCidLoading(true);
    setCinterLoading(false);
    try {
      // Phase 1 : CIntra seulement
      const r1 = await reportingApi.cidRun(closingDate, fichier, decs, false);
      setCidData(r1.data);
      setRows(r1.data.rows || []);
      setTab('cid');
      setCidLoading(false);

      // Phase 2 : CInter complet en arrière-plan
      setCinterLoading(true);
      try {
        const r2 = await reportingApi.cidRun(closingDate, fichier, decs, true);
        setCidData(r2.data);
        setRows(r2.data.rows || []);
      } catch {
        // garder les résultats Phase 1
      } finally {
        setCinterLoading(false);
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur CID.' });
      setCidLoading(false);
    }
  };

  // ── Corrections automatiques ─────────────────────────────────────────────────

  const handleCorrectionsAuto = async () => {
    setCerberLoading(true);
    setSaveMsg(null);
    try {
      const res = await reportingApi.cerberPipeline(closingDate, decs);
      const d = res.data;
      const total = d.total_corrections ?? d.corrections_applied ?? 0;
      const unified = d.unified;

      if (unified) {
        // Pipeline unifié cerber_engine — affichage détaillé
        const steps = unified.by_step || {};
        const byRule = unified.by_rule || {};
        const nbOk = unified.fichiers_ok ?? 0;
        const nbNok = unified.fichiers_nok ?? 0;
        const nbOsc = unified.oscillations_fixed?.length ?? steps.oscillations ?? 0;
        const nbRej = unified.rejected_files?.length ?? 0;
        const elapsed = unified.elapsed_seconds ?? '?';

        const parts = [];
        if (steps.fix_structure) parts.push(`structure=${steps.fix_structure}`);
        if (steps.cquali) parts.push(`cquali=${steps.cquali}`);
        if (steps.recf100) parts.push(`recf100=${steps.recf100}`);
        if (byRule.cinter) parts.push(`CInter=${byRule.cinter}`);
        if (byRule.cintra) parts.push(`CIntra=${byRule.cintra}`);
        if (byRule.ccplex) parts.push(`CCplex=${byRule.ccplex}`);

        let msg = `Pipeline CERBER (${elapsed}s) : ${total} correction(s)`;
        if (parts.length) msg += ` — ${parts.join(', ')}`;
        if (nbOk || nbNok) msg += ` | Fichiers: ${nbOk} OK, ${nbNok} NOK`;
        if (nbOsc) msg += ` | ${nbOsc} oscillation(s)`;
        if (nbRej) msg += ` | ${nbRej} rejeté(s)`;

        setSaveMsg({ type: nbNok > 0 ? 'warning' : 'success', text: msg });
        setCerberNokDetails(unified.nok_details || []);
      } else {
        setCerberNokDetails([]);
        // Ancien pipeline
        if (total > 0) {
          setSaveMsg({ type: 'success', text: `Corrections auto : ${total} correction(s) appliquée(s).` });
        } else {
          setSaveMsg({ type: 'success', text: 'Corrections auto : aucune correction nécessaire.' });
        }
      }
      await loadAll();
      if (tab === 'history') loadHistory();
    } catch (err) {
      setCerberNokDetails([]);
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur corrections auto.' });
    } finally {
      setCerberLoading(false);
    }
  };

  // ── Pipeline CERBER (Import ZIP) ─────────────────────────────────────────────

  const handleCerberZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setZipUploading(true);
    setZipResult(null);
    setSaveMsg(null);
    try {
      const res = await reportingApi.cerberProcessZip(file, closingDate, cerberSaveToDb);
      const d = res.data;
      setZipResult(d);
      const total = d.stats?.corrections_total ?? 0;
      const ko = d.stats?.controles_ko ?? 0;
      setSaveMsg({
        type: ko > 0 ? 'warning' : 'success',
        text: `Pipeline CERBER terminé — ${total} correction(s) appliquée(s), ${ko} contrôle(s) encore KO.`,
      });
      if (cerberSaveToDb && d.saved_to_db?.length > 0) await loadAll();
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur pipeline CERBER.' });
    } finally {
      setZipUploading(false);
      e.target.value = '';
    }
  };

  const handleDownloadCerberZip = () => {
    if (!zipResult?.corrected_zip_b64) return;
    const binary = atob(zipResult.corrected_zip_b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = zipResult.corrected_zip_filename || 'cerber-corrected.zip';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAutoCorrect = (ruleCode) => {
    setConfirmAuto(ruleCode);
  };

  const handleConfirmAuto = async () => {
    const ruleCode = confirmAuto;
    setConfirmAuto(null);
    setAutoLoading(ruleCode);
    try {
      const res = await reportingApi.cidCorrectAuto(closingDate, fichier, { rule_code: ruleCode }, decs);
      const d = res.data;
      await loadAll();
      if (d.corrections_applied > 0) {
        setSaveMsg({ type: 'success', text: `${d.corrections_applied} correction(s) appliquée(s) pour ${ruleCode}.` });
        loadHistory();
      } else {
        setSaveMsg({ type: 'warning', text: d.message || 'Aucune correction appliquée.' });
      }
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur correction auto.' });
    } finally {
      setAutoLoading(null);
    }
  };

  // ── History ──────────────────────────────────────────────────────────────────

  const handleRevert = async (logId) => {
    setRevertLoading(logId);
    try {
      await reportingApi.cidRevert(logId);
      await loadAll();
      await loadHistory();
      setSaveMsg({ type: 'success', text: 'Correction annulée avec succès.' });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de l\'annulation.' });
    } finally {
      setRevertLoading(null);
    }
  };

  const handleBatchRevert = async (actionFilter) => {
    setBatchRevertLoading(true);
    try {
      const res = await reportingApi.cidBatchRevert(
        closingDate, fichier,
        actionFilter ? [actionFilter] : null,
      );
      const count = res.data?.reverted_count ?? 0;
      const errs = res.data?.errors ?? [];
      let msg = `${count} correction(s) annulée(s) pour ${fichier}.`;
      if (errs.length) msg += ` ${errs.length} erreur(s): ${errs.slice(0, 3).join(', ')}...`;
      setSaveMsg({ type: errs.length ? 'warning' : 'success', text: msg });
      await loadAll();
      await loadHistory();
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur lors de l\'annulation par lot.' });
    } finally {
      setBatchRevertLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const pendingEdits = Object.keys(edits).length;

  const cidStatus = cidData
    ? cidData.all_ok
      ? { color: 'text-emerald-700 bg-emerald-100', label: 'Tous OK', icon: <CheckCircle2 size={15} /> }
      : { color: 'text-red-700 bg-red-100', label: `${cidData.nok_count} NOK`, icon: <XCircle size={15} /> }
    : null;

  const TABS = [
    { id: 'data', label: 'Données', icon: <Table2 size={15} /> },
    { id: 'cid', label: 'Contrôles CID', icon: <ShieldCheck size={15} /> },
    { id: 'import', label: 'Vérification ZIP', icon: <FileArchive size={15} /> },
    { id: 'history', label: 'Historique', icon: <History size={15} /> },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-6 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/reporting/consult')}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"
          >
            <ArrowLeft size={15} /> Retour
          </button>
          <h1 className="text-2xl font-semibold text-slate-900">Contrôles CID</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-mono font-semibold text-slate-700">{fichier}</span>
            {decs && <> · <span className="font-mono">{decs}</span></>}
            {' · '}Arrêté au <strong>{formatDate(closingDate)}</strong>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {cidStatus && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-full ${cidStatus.color}`}>
              {cidStatus.icon} {cidStatus.label}
            </span>
          )}
          <button
            onClick={handleRunCid}
            disabled={cidLoading || loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#E20613] text-white text-sm font-medium hover:bg-[#c90511] rounded disabled:opacity-50"
          >
            {cidLoading ? <RefreshCw size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
            {cidLoading ? 'Vérification…' : 'Lancer les contrôles'}
          </button>

          {/* Corrections automatiques */}
          <button
            onClick={handleCorrectionsAuto}
            disabled={cerberLoading || loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 rounded disabled:opacity-50"
          >
            {cerberLoading ? <RefreshCw size={15} className="animate-spin" /> : <Wand2 size={15} />}
            {cerberLoading ? 'Corrections…' : 'Corrections auto'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {saveMsg && (
        <Alert type={saveMsg.type}>
          <div className="flex justify-between items-center">
            <span>{saveMsg.text}</span>
            <button onClick={() => { setSaveMsg(null); setCerberNokDetails([]); }} className="ml-4 opacity-60 hover:opacity-100"><X size={14} /></button>
          </div>
        </Alert>
      )}

      {/* Détails fichiers NOK */}
      {cerberNokDetails.length > 0 && (
        <div className="border border-amber-300 rounded bg-amber-50 p-3 text-sm">
          <div className="font-semibold text-amber-800 mb-2">Fichiers non conformes après corrections ({cerberNokDetails.length})</div>
          <div className="space-y-2">
            {cerberNokDetails.map(nok => (
              <details key={nok.fichier} className="border border-amber-200 rounded bg-white">
                <summary className="px-3 py-2 cursor-pointer font-mono font-semibold text-amber-900 flex items-center gap-2">
                  <span>{nok.fichier}</span>
                  <span className="text-xs font-normal text-amber-600">({nok.blocking_count} règle(s) en écart)</span>
                </summary>
                <ul className="px-4 py-2 space-y-1 text-xs text-slate-700">
                  {nok.issues.map((iss, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-mono text-blue-700 shrink-0">{iss.rule}</span>
                      <span>{iss.detail}</span>
                      {iss.ecart != null && <span className="text-red-600 shrink-0">écart={iss.ecart}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Barre d'édition */}
      {editMode && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 px-4 py-3 rounded">
          <Pencil size={15} className="text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 flex-1">
            Mode édition actif.{' '}
            {pendingEdits > 0 ? <strong>{pendingEdits} modification(s) en attente.</strong> : 'Modifiez les cellules, puis enregistrez.'}
          </span>
          <button
            onClick={handleCancelEdit}
            className="px-3 py-1.5 text-sm border border-amber-400 text-amber-700 hover:bg-amber-100 rounded"
          >
            <X size={13} className="inline mr-1" />Annuler
          </button>
          <button
            onClick={handleSaveEdits}
            disabled={saving || pendingEdits === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#E20613] text-white hover:bg-[#c90511] rounded disabled:opacity-50"
          >
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}

      {/* Card principale */}
      <div className="bg-white border border-slate-200 shadow-sm rounded">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-2 pt-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? 'border-[#E20613] text-[#004A99]'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.icon} {t.label}
              {t.id === 'cid' && cidData && !cidData.all_ok && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs bg-red-100 text-red-700 rounded-full font-bold">
                  {cidData.nok_count}
                </span>
              )}
              {t.id === 'history' && history.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs bg-slate-200 text-slate-600 rounded-full font-bold">
                  {history.length}
                </span>
              )}
            </button>
          ))}

          <div className="flex-1" />

          {/* Actions de l'onglet Data */}
          {tab === 'data' && !loading && !editMode && (
            <div className="flex items-center gap-2 pb-2">
              <button
                onClick={() => setEditMode(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 rounded"
              >
                <Pencil size={13} /> Éditer les valeurs
              </button>
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="p-4">
          {loading ? (
            <Spinner />
          ) : error ? (
            <Alert type="error">{error}</Alert>
          ) : tab === 'data' ? (
            <DataTab
              rows={rows}
              columns={columns}
              editMode={editMode}
              edits={edits}
              onEditCell={handleEditCell}
              nokPostes={nokPostes}
              cinterLoading={cinterLoading}
            />
          ) : tab === 'cid' ? (
            <CIDTab
              cidData={cidData}
              onAutoCorrect={handleAutoCorrect}
              autoLoading={autoLoading}
              cinterLoading={cinterLoading}
            />
          ) : tab === 'import' ? (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                <h3 className="text-base font-semibold text-blue-900 mb-1 flex items-center gap-2">
                  <ShieldCheck size={16} /> Vérification ZIP — Contrôle &amp; Correction COBAC
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Contrôle final de la soumission COBAC. Uploadez le ZIP contenant tous les TXT,
                  le pipeline CERBER v5 applique les corrections automatiques (structure, CQuali, RECF100, CInter, CIntra, CCplex)
                  et génère un ZIP corrigé à télécharger. Mêmes KPI que l'outil expert.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-medium rounded cursor-pointer hover:bg-[#003a7a] ${zipUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {zipUploading ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />}
                    {zipUploading ? 'Vérification en cours…' : 'Uploader ZIP & lancer la vérification'}
                    <input type="file" accept=".zip" onChange={handleCerberZip} className="hidden" disabled={zipUploading} />
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={cerberSaveToDb}
                      onChange={(e) => setCerberSaveToDb(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    Sauvegarder les fichiers corrigés en base
                  </label>
                </div>
              </div>

              {zipResult?.stats && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Fichiers analysés', value: zipResult.stats.fichiers_analyses },
                      { label: 'Fichiers OK', value: zipResult.stats.fichiers_ok, color: 'text-emerald-700' },
                      { label: 'Corrections', value: zipResult.stats.corrections_total, color: zipResult.stats.corrections_total > 0 ? 'text-amber-700' : 'text-emerald-700' },
                      { label: 'Contrôles KO', value: zipResult.stats.controles_ko, color: zipResult.stats.controles_ko > 0 ? 'text-red-700' : 'text-emerald-700' },
                    ].map(({ label, value, color = 'text-slate-800' }) => (
                      <div key={label} className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${color}`}>{value}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Détail des corrections</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                      {[
                        ['Structure', zipResult.stats.corrections_structure],
                        ['CQuali', zipResult.stats.corrections_cquali],
                        ['CInter', zipResult.stats.corrections_cinter],
                        ['CIntra', zipResult.stats.corrections_cintra],
                        ['CCplex', zipResult.stats.corrections_ccplex],
                      ].map(([type, count]) => (
                        <div key={type} className="flex justify-between items-center bg-slate-50 rounded px-3 py-2">
                          <span className="text-slate-600 font-medium">{type}</span>
                          <span className={`font-bold ${count > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>{zipResult.stats.nb_passes} passe(s)</span>
                      <span>·</span>
                      <span>{zipResult.stats.elapsed_sec}s</span>
                      {(zipResult.stats.oscillations_detected ?? 0) > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-amber-600">Oscillations : {zipResult.stats.oscillations_detected}</span>
                        </>
                      )}
                      {(zipResult.stats.rejected_files ?? 0) > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-red-600">Rejetés : {zipResult.stats.rejected_files}</span>
                        </>
                      )}
                      {zipResult.stats.fichiers_manquants?.length > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-amber-600">Manquants : {zipResult.stats.fichiers_manquants.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={handleDownloadCerberZip}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700"
                    >
                      <Download size={15} /> Télécharger le ZIP corrigé
                    </button>
                    {zipResult.saved_to_db?.length > 0 && (
                      <span className="text-sm text-emerald-700">
                        {zipResult.saved_to_db.length} fichier(s) sauvegardés en base : {zipResult.saved_to_db.join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            historyLoading ? <Spinner /> : (
              <HistoryTab
                history={history}
                onRevert={handleRevert}
                onBatchRevert={handleBatchRevert}
                revertLoading={revertLoading}
                batchRevertLoading={batchRevertLoading}
              />
            )
          )}
        </div>
      </div>

      {/* Modal confirmation auto-correction */}
      {confirmAuto && (
        <ConfirmModal
          title="Confirmer la correction automatique"
          message={`Appliquer la correction automatique pour la règle "${confirmAuto}" sur tous les postes NOK ? Les valeurs modifiées seront loggées et pourront être annulées.`}
          onConfirm={handleConfirmAuto}
          onCancel={() => setConfirmAuto(null)}
          loading={!!autoLoading}
        />
      )}
    </div>
  );
};

export default CIDPage;
